export interface GetSelectListResponse<T> {
    disables: boolean,
    selected: boolean,
    name: string,
    value: T
}

export interface ErrorResponse {
    type: string,
    title: string,
    status: number,
    detail: string,
    instance: string,
    /** A failed FluentValidation: messages by property path, e.g. "PaymentDataForExport[7].RecipientBankData". */
    errors?: Record<string, string[]>
}

export interface Response<T> {
    value: T,
    message: string,
    status: string
}

export interface MainResponse {
    id: string,
    message: string,
}

export interface NoValue {
}

export interface RejectedValue {
    message: string;
    status?: number;
    errors?: Record<string, string[]>;
}

export interface ListModel<T> {
    value: T | null,
    label: string,
    isActive?: boolean | null,
    isFixed?: boolean
}

export interface ListModelWithIntValue<T> extends ListModel<T> {
    intValue: number | null;
}

export interface ListModelWithBoolValue<T> extends ListModel<T> {
    boolValue: boolean | null,
}

export interface ListModelWithCurrency<T> {
    value: T | null,
    label: string,
    currency: string,
    totalGross: number | null,
    creditedAmount: number | null,
}

export interface ListModelWithType<T> {
    value: T | null,
    label: string,
    type: string,
}

export interface ListModelWithDetail<T> {
    value: T | null,
    label: string,
    detail: string | null,
}

export interface ListModelWithDate<T> {
    value: T | null,
    label: string,
    date: Date | null,
}

export interface ListModelWithDateAndBoolValue<T> {
    value: T | null,
    label: string,
    date: Date | null,
    boolValue: boolean | null,
}

export interface ListFinancePartnerModel<T> {
    value: T | null,
    label: string,
    type: "partner" | "tenant",
}

export interface GroupListModel<T> {
    label: string,
    options: ListModel<T>[]
}

export interface ListModelWithItems<T> {
    value: T | null,
    label: string,
    items: ListModel<T>[]
}

export interface TranslationUDF {
    language: string
    translation: string,
}

export interface NameTranslationUDF {
    language: string
    translationName: string
    translationDescription: string
}

export interface UnitTranslationUDF {
    language: string,
    translationName: string,
    translationFullName?: string,
    translationDescription?: string
}

export interface UnitTranslationList {
    value: number,
    label: string,
    fullName: string,
    isActive: boolean
}

export interface TranslationList {
    id: string,
    defaultLanguage: string,
    names: TranslationUDF[],
    isActive: boolean,
    type?: number | null,
    number?: string | null,
    boolValue?: boolean | null
}

export interface CreateReferenceData {
    name: string
}

export interface ImportHubspotListResponse {
    message: string;
    importedPartners: number;
    importedLocations: number;
    importedIndustrySectors: number;
    importedCompanyType: number;
    start: Date;
    end: Date;
}

export interface FilteredDropDownResponse {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    data: ListModel<string>[] | [];
    selectedData?: ListModel<string>[] | null;
}

export interface FilteredDropDownModel {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    data: ListModel<string>[] | [];
    loading: boolean;
    error: string | null;
}

export const FilteredDropDownModelInit: FilteredDropDownModel = {
    data: [],
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    loading: false,
    error: null,
}

export interface ParamsForFilteredDropDown {
    inputValue: string | null;
    page: number;
    pageSize: number;
}

export interface FilteredDropDownRequest {
    params: ParamsForFilteredDropDown;
    selectedIds?: string[] | null;
}

//Filtered drop down whose options carry an additional reference (e.g. the company of a contact)
export interface FilteredDropDownDetailResponse {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    data: ListModelWithDetail<string>[] | [];
    selectedData?: ListModelWithDetail<string>[] | null;
}

//Filtered drop down with type
export interface FilteredDropDownWithTypeResponse {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    data: ListFinancePartnerModel<string>[] | [];
}

export interface FilteredDropDownWithTypeModel {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    data: ListFinancePartnerModel<string>[] | [];
    loading: boolean;
    error: string | null;
}

export const FilteredDropDownWithTypeModelInit: FilteredDropDownWithTypeModel = {
    data: [],
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    loading: false,
    error: null,
}

export interface Pagination {
    pageIndex: number;
    pageSize: number;
}

export interface ColumnFilter {
    id: string;
    value: unknown;
}

export interface ColumnSort {
    desc: boolean;
    id: string;
}

export interface TableQueryParams {
    globalFilter: string;
    pagination: Pagination;
    propertyFilters: ColumnFilter[] | null;
    sorting: ColumnSort | null
}

export interface YearWeekModel {
    year: number;
    week: number;
}

export interface GetPermissionsResponse {
    createEditTenants: ListModel<string>[]
    reactivateDeactivateTenants: ListModel<string>[]
    deleteTenants: ListModel<string>[]
    createNextTenants?: ListModel<string>[]
}

export interface OutlookItem {
    id?: any; // Assuming OutlookItem has an ID property
    createdDateTime?: any; // ISO 8601 string
    lastModifiedDateTime?: any; // ISO 8601 string
    // Add other OutlookItem properties here
}

export interface EmailAttachment {
    contentType: string,
    isInline: boolean,
    lastModifiedDateTime: Date,
    name: string,
    size: number,
    additionalData: any,
    backingStore: any
    id: string,
    odataType: string
}

// Define the Message model
export interface Message extends OutlookItem {
    attachments?: EmailAttachment[];
    subject?: any;
    body?: any;
    sender?: any;
    from?: any;
    bodyPreview?: string;
    toRecipients?: any[];
    ccRecipients?: any[];
    // Graph only populates bcc for messages read from the sender's own mailbox,
    // so this is empty on everything that arrived in the Inbox.
    bccRecipients?: any[];
    sentDateTime?: any; // ISO 8601 string
    receivedDateTime?: any; // ISO 8601 string
    conversationId?: string | null;
    // Base64 blob that encodes the reply chain of a conversation; see email-thread-utils.
    conversationIndex?: string | null;
}